import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { OtherLegal } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type OtherLegalStoreAction =
  | { type: "SET_OTHER_LEGAL"; payload: OtherLegal[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_OTHER_LEGAL"; payload: OtherLegal }
  | { type: "DELETE_OTHER_LEGAL"; payload: string }
  | { type: "SET_OTHER_LEGAL_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_OTHER_LEGAL_SAVING_NEW"; payload: boolean };

// Reducer Function
const otherLegalStoreReducer = (
  state: OtherLegalStoreState,
  action: OtherLegalStoreAction
): OtherLegalStoreState => {
  switch (action.type) {
    case "SET_OTHER_LEGAL":
      return {
        ...state,
        otherLegal: action.payload,
        otherLegalLoading: false,
      };

    case "SET_LOADING":
      return { ...state, otherLegalLoading: action.payload };

    case "ADD_OR_UPDATE_OTHER_LEGAL":
      const index = state.otherLegal.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedOtherLegal = [...state.otherLegal];
      if (index > -1) {
        updatedOtherLegal[index] = action.payload;
      } else {
        updatedOtherLegal.push(action.payload);
      }
      return { ...state, otherLegal: updatedOtherLegal };

    case "DELETE_OTHER_LEGAL":
      return {
        ...state,
        otherLegal: state.otherLegal.filter((l) => l.id !== action.payload),
      };
    case "SET_OTHER_LEGAL_FILES_TO_UPLOAD":
      return { ...state, otherLegalFilestoUpload: action.payload };
    case "SET_OTHER_LEGAL_SAVING_NEW":
      return { ...state, otherLegalSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface OtherLegalStoreState {
  otherLegal: OtherLegal[];
  otherLegalLoading: boolean;
  otherLegalSavingNew: boolean;
  otherLegalFilestoUpload: File[];
}

// Hook
export const useOtherLegalStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(otherLegalStoreReducer, {
    otherLegal: [],
    otherLegalLoading: true,
    otherLegalFilestoUpload: [],
    otherLegalSavingNew: false,
  });

  const deleteOtherLegalById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchOtherLegalData = useV3GenericGetData<OtherLegal[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createOtherLegal = useV3Create<OtherLegal>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateOtherLegal = useV3Update<OtherLegal>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<OtherLegal>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleOtherLegalFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.otherLegalFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "other-legal",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_OTHER_LEGAL",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_OTHER_LEGAL_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.otherLegalFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadOtherLegalDocument = useCallback(
    async (otherLegal_id: string, document_id: string | null) => {
      if (!document_id || !otherLegal_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document_id,
        entity: "other-legal",
        entity_id: otherLegal_id,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in otherLegal objects' documents array
            downloadFileFromS3(
              res,
              state.otherLegal
                ?.find((p) => p.id === otherLegal_id)
                ?.documents?.find((d) => d.document_id === document_id)
                ?.document_name || "Snug_Download.error"
            );
          } else {
            reportError(new Error(`Document Error: Failed to get document with message "No response"`));
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
        });
    },
    [downloadDocument, state.otherLegal, ud_id]
  );

  const handleDeleteOtherLegalDocument = useCallback(
    async (
      otherLegal_id: string,
      document_id: string | null
    ): Promise<void> => {
      if (!document_id || !otherLegal_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "other-legal",
          entity_id: otherLegal_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the otherLegal object
          const updatedOtherLegal = state.otherLegal.map((p) => {
            if (p.id === otherLegal_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_OTHER_LEGAL",
            payload: updatedOtherLegal,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.otherLegal, deleteDocumentById, ud_id]
  );

  const handleOtherLegalFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_OTHER_LEGAL_FILES_TO_UPLOAD",
        payload: [...state.otherLegalFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.otherLegalFilestoUpload]
  );

  const handleOtherLegalFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.otherLegalFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_OTHER_LEGAL_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.otherLegalFilestoUpload]
  );

  const handleOtherLegalFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_OTHER_LEGAL_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchOtherLegalItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchOtherLegalData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "other-legal",
        });

        if (isMounted()) {
          dispatch({ type: "SET_OTHER_LEGAL", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchOtherLegalData, isMounted]
  );

  const handleAddOrUpdateOtherLegalSingleCall = useCallback(
    async (otherLegalItem: OtherLegal): Promise<OtherLegal | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(otherLegalItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateOtherLegal(
            {
              ud_id: ud_id,
              entity: "other-legal",
              entity_id: otherLegalItem.id || undefined,
            },
            otherLegalItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createOtherLegal(
            { ud_id: ud_id, entity: "other-legal" }, // Adjust according to your actual API parameter requirements
            otherLegalItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_OTHER_LEGAL", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save otherLegal item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createOtherLegal, updateOtherLegal]
  );

  const handleAddOrUpdateOtherLegal = useCallback(
    async (otherLegalItem: OtherLegal) => {
      dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: true });
      if (state.otherLegalFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateOtherLegalSingleCall(
            otherLegalItem
          );
          if (res) {
            await handleOtherLegalFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit otherLegal data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateOtherLegalSingleCall(otherLegalItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit otherLegal data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateOtherLegalSingleCall,
      state.otherLegalFilestoUpload,
      handleOtherLegalFilesUpload,
    ]
  );

  const handleDeleteOtherLegal = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteOtherLegalById({
          ud_id: ud_id,
          entity: "other-legal",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_OTHER_LEGAL", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_OTHER_LEGAL_SAVING_NEW", payload: false });
      }
    },
    [deleteOtherLegalById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchOtherLegalItems(ud_id); // Fetch all otherLegal items
    }
  }, [ fetchOtherLegalItems, ud_id]);

  return {
    otherLegal: state.otherLegal,
    otherLegalLoading: state.otherLegalLoading,
    otherLegalSavingNew: state.otherLegalSavingNew,
    otherLegalFilestoUpload: state.otherLegalFilestoUpload,
    handleAddOrUpdateOtherLegal,
    handleDeleteOtherLegal,
    handleOtherLegalFileRemove,
    handleOtherLegalFilesDrop,
    handleOtherLegalFilesRemoveAll,
    handleDownloadOtherLegalDocument,
    handleDeleteOtherLegalDocument,
    handleOtherLegalFilesUpload,
  };
};
